import React from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';

import { hasContractEntered, isOpen, isValidToCancel, isValidToSell, useNewRowTransition } from '@deriv/shared';
import { TContractInfo } from '@deriv/shared/src/utils/contract/contract-types';
import { Localize } from '@deriv-com/translations';

import Text from '../../text';
import { TGetCardLables } from '../../types';

import ContractCardSell from './contract-card-sell';
import MultiplierCloseActions from './multiplier-close-actions';

export type TCardFooterPropTypes = {
    contract_info: TContractInfo;
    getCardLabels: TGetCardLables;
    is_multiplier?: boolean;
    is_positions?: boolean;
    is_sell_requested: boolean;
    is_lookbacks?: boolean;
    onClickCancel: (contract_id?: number) => void;
    onClickSell: (contract_id?: number) => void;
    onFooterEntered?: () => void;
    server_time: moment.Moment;
    should_show_transition?: boolean;
};

const CardFooter = ({
    contract_info,
    getCardLabels,
    is_multiplier,
    is_positions,
    is_sell_requested,
    is_lookbacks,
    onClickCancel,
    onClickSell,
    onFooterEntered,
    server_time,
    should_show_transition,
}: TCardFooterPropTypes) => {
    const { in_prop } = useNewRowTransition(!!should_show_transition);
    const nodeRef = React.useRef(null);

    // Only run when should_show_transition changes to avoid unnecessary re-renders
    React.useEffect(() => {
        if (!should_show_transition && onFooterEntered) {
            onFooterEntered();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [should_show_transition]);

    const is_valid_to_cancel = isValidToCancel(contract_info);

    const should_show_sell = hasContractEntered(contract_info) && isOpen(contract_info);
    const should_show_sell_note = is_lookbacks && isValidToSell(contract_info) && should_show_sell;

    if (!should_show_sell) {
        return null;
    }

    const footer_content = (
        <div className='dc-contract-card-item__footer'>
            {is_multiplier ? (
                <div
                    className={classNames('dc-contract-card__sell-button', {
                        'dc-contract-card__sell-button--has-cancel-btn': is_valid_to_cancel,
                        'dc-contract-card__sell-button--positions': is_positions,
                    })}
                >
                    <MultiplierCloseActions
                        className='dc-btn--sell'
                        contract_info={contract_info}
                        getCardLabels={getCardLabels}
                        is_sell_requested={is_sell_requested}
                        onClickCancel={onClickCancel}
                        onClickSell={onClickSell}
                        server_time={server_time}
                    />
                </div>
            ) : (
                <React.Fragment>
                    <div
                        className={classNames('dc-contract-card__sell-button', {
                            'dc-contract-card__sell-button--positions': is_positions,
                        })}
                    >
                        <ContractCardSell
                            contract_info={contract_info}
                            is_sell_requested={is_sell_requested}
                            getCardLabels={getCardLabels}
                            onClickSell={onClickSell}
                        />
                    </div>
                    {should_show_sell_note && (
                        <Text as='div' size='xxxs' color='less-prominent' line_height='s' className='lookbacks--note'>
                            <Localize
                                i18n_default_text='<0>Note:</0> Contract will be sold at the prevailing market price when the request is received by our servers. This price may differ from the indicated price.'
                                components={[<strong key={0} />]}
                            />
                        </Text>
                    )}
                </React.Fragment>
            )}
        </div>
    );

    // If transitions are disabled, render directly
    if (!should_show_transition) {
        return footer_content;
    }

    // Use CSSTransition only when transitions are enabled
    return (
        <CSSTransition
            in={in_prop}
            timeout={250}
            classNames={{
                enter: 'dc-contract-card__sell-button--enter',
                enterDone: 'dc-contract-card__sell-button--enter-done',
                exit: 'dc-contract-card__sell-button--exit',
            }}
            onEntered={onFooterEntered}
            nodeRef={nodeRef}
            unmountOnExit
        >
            <div ref={nodeRef}>{footer_content}</div>
        </CSSTransition>
    );
};

export default CardFooter;
